import { StrictMode, Suspense, lazy } from "react"
import { createRoot } from "react-dom/client"
import { MotionConfig } from "framer-motion"
// Self-hosted fonts (replaces the Google Fonts CDN): Bitter (serif, incl. italic
// axis) + Red Hat Display (sans), both variable.
import "@fontsource-variable/bitter/index.css"
import "@fontsource-variable/bitter/wght-italic.css"
import "@fontsource-variable/red-hat-display/index.css"
import "./index.css"
import App from "./App.tsx"
import { ErrorBoundary } from "./ErrorBoundary.tsx"
import { GameStateProvider } from "./GameStateContext.tsx"
import { WebSocketProvider } from "./WebSocketProvider.tsx"
import { AppNotices } from "./components/AppNotices.tsx"
import { initSentry } from "./sentry.ts"
import { DEBUG_ENABLED, installDebugConsoleApi } from "./debug/debugFlag.ts"
import { parseRoute } from "./route.ts"
import TvApp from "./tv/TvApp.tsx"

initSentry()

// Always expose the devtools switch (a few lines); the harness itself is
// lazy-loaded only when the flag is on, so it stays out of the normal bundle.
installDebugConsoleApi()
const DebugOverlay = DEBUG_ENABLED ? lazy(() => import("./debug/DebugOverlay.tsx")) : null

/**
 * Registering the worker is what makes Misquote installable to a home screen —
 * that's the whole reason it exists. It caches nothing but the offline page
 * (see src/sw.ts), so there's no update dance to run here: a new build is picked
 * up on the next load like any ordinary web page.
 */
if ("serviceWorker" in navigator && import.meta.env.PROD) {
    const registerWorker = () => {
        navigator.serviceWorker.register("/sw.js", { scope: "/" }).catch(() => {
            // Installability is a bonus, never a requirement — a worker that
            // won't register must not take the game down with it.
        })
    }

    // Hold off until load so the worker isn't competing with the bundle and
    // fonts for bandwidth. The readyState check is belt to that braces: a
    // listener attached after load has already fired never runs at all, which
    // silently costs us installability if this module ever executes late.
    if (document.readyState === "complete") registerWorker()
    else window.addEventListener("load", registerWorker, { once: true })
}

/**
 * The display and the player app are different animals sharing a codebase.
 *
 * A TV mounts none of the player machinery — no game state, no socket provider,
 * no rejoin token, no notices, no service worker to install — because it has no
 * identity to keep and nothing to send. What it does share is everything that
 * makes it look like Misquote: the theme, the type, the paper, the motion
 * config. That's the split worth having.
 */
const route = parseRoute(window.location.pathname, window.location.search)

createRoot(document.getElementById("root")!).render(
    // The WS connection is opened on user action, not on mount, so StrictMode's
    // dev-only double-invoke of effects doesn't double-open the socket.
    <StrictMode>
        {/* `reducedMotion="user"` is framer-motion's own handling of the OS
            setting: it drops transform/layout animation but keeps opacity, so
            screens still cross-fade instead of snapping. The CSS keyframe half
            of this lives in index.css. */}
        <MotionConfig reducedMotion="user">
            <ErrorBoundary>
                {route.kind === "tv" ? (
                    <TvApp initialCode={route.code}/>
                ) : (
                    <GameStateProvider>
                        <WebSocketProvider>
                            <App/>
                            <AppNotices/>
                            {DebugOverlay && (
                                <Suspense fallback={null}>
                                    <DebugOverlay/>
                                </Suspense>
                            )}
                        </WebSocketProvider>
                    </GameStateProvider>
                )}
            </ErrorBoundary>
        </MotionConfig>
    </StrictMode>,
)
